import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, GoogleLogo, Envelope, Phone, ArrowSquareOut } from "@phosphor-icons/react";
import {
  signInWithGoogle,
  requestEmailLink,
  startPhoneSignIn,
  clearRecaptcha,
} from "@/lib/firebase";
import ContinueWithSocialPulse from "@/components/ContinueWithSocialPulse";
import { socialPulseEnabled } from "@/lib/socialpulseOidc";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
  { id: "google", label: "Google", Icon: GoogleLogo },
  { id: "email", label: "Email link", Icon: Envelope },
  { id: "phone", label: "Phone", Icon: Phone },
];

export default function LoginModal({ open, onClose }) {
  const { celebrateLogin } = useAuth();
  const [tab, setTab] = useState("google");
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const confirmationRef = useRef(null);

  // Policy checkboxes — read the linked docs and tick both to enable buttons
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const agreed = termsChecked && privacyChecked;

  const close = () => {
    if (busy) return;
    clearRecaptcha();
    setEmailSent(false); setOtpSent(false); setOtp(""); setEmail(""); setPhone("");
    setTermsChecked(false); setPrivacyChecked(false);
    onClose?.();
  };

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const requireAgreed = () => {
    if (!agreed) {
      toast.error("Please tick both boxes to agree to the Terms and Privacy Notice.");
      return false;
    }
    return true;
  };

  const handleGoogle = async () => {
    if (!requireAgreed()) return;
    setBusy(true);
    try {
      await signInWithGoogle();
      toast.success("Signed in with Google");
      close();
      celebrateLogin();
    } catch (e) {
      toast.error(e?.message || "Google sign-in failed");
    } finally { setBusy(false); }
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!requireAgreed()) return;
    if (!email) return;
    setBusy(true);
    try {
      await requestEmailLink(email.trim());
      setEmailSent(true);
      toast.success("Sign-in link sent — check your inbox.");
    } catch (err) {
      toast.error(err?.message || "Could not send link");
    } finally { setBusy(false); }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!requireAgreed()) return;
    if (!phone) return;
    setBusy(true);
    try {
      const p = phone.trim();
      const e164 = p.startsWith("+") ? p : `+${p.replace(/\D/g, "")}`;
      const confirmation = await startPhoneSignIn(e164, "recaptcha-container");
      confirmationRef.current = confirmation;
      setOtpSent(true);
      toast.success("OTP sent");
    } catch (err) {
      toast.error(err?.message || "Could not send OTP");
      clearRecaptcha();
    } finally { setBusy(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || !confirmationRef.current) return;
    setBusy(true);
    try {
      await confirmationRef.current.confirm(otp.trim());
      toast.success("Signed in via phone");
      close();
      celebrateLogin();
    } catch (err) {
      toast.error(err?.message || "Invalid code");
    } finally { setBusy(false); }
  };

  const modal = (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="login-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1000] flex items-center justify-center px-3 md:px-4"
          style={{ height: "100dvh" }}
        >
          <div className="absolute inset-0 bg-black/75 backdrop-blur-md" onClick={close} />
          <motion.div
            initial={{ y: 30, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 20, scale: 0.98, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            className="relative w-full max-w-md flex flex-col bg-[color:var(--np-bg)] border border-black/15 shadow-2xl rounded-2xl overflow-hidden"
            style={{ maxHeight: "88dvh" }}
          >
            {/* Sticky header — tabs always visible */}
            <div className="shrink-0 relative px-6 md:px-8 pt-5 md:pt-6 pb-3 border-b border-black/10 bg-[color:var(--np-bg)]">
              <button
                onClick={close}
                data-testid="login-modal-close"
                className="absolute top-4 right-4 p-1 text-[color:var(--np-text-2)] hover:text-[color:var(--np-text)]"
                aria-label="Close"
              >
                <X size={18} weight="thin" />
              </button>
              <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-1">
                SocialPulse · Unified Sign-in
              </div>
              <h2 className="font-serif-display text-2xl leading-none text-[color:var(--np-text)]">
                Join the archive.
              </h2>
              <div className="mt-4 flex gap-1">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`login-tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`flex items-center gap-2 px-3 py-2 font-mono-tech text-[10px] tracking-[0.25em] uppercase transition-colors border-b-2 ${
                      tab === t.id
                        ? "border-[color:var(--np-brand)] text-[color:var(--np-text)]"
                        : "border-transparent text-[color:var(--np-text-2)] hover:text-[color:var(--np-text)]"
                    }`}
                  >
                    <t.Icon size={14} weight="thin" />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Scrollable body */}
            <div className="grow overflow-y-auto px-6 md:px-8 py-5">
              {tab === "google" && (
                <div className="space-y-4">
                  <p className="text-xs text-[color:var(--np-text-2)]">
                    Fastest option. We only use your name, email and avatar.
                  </p>
                  {socialPulseEnabled() && (
                    <>
                      <ContinueWithSocialPulse returnTo={typeof window !== "undefined" ? window.location.pathname + window.location.hash : "/"} disabled={!agreed} />
                      <div className="relative py-1">
                        <div className="absolute inset-0 flex items-center" aria-hidden="true">
                          <div className="w-full border-t border-black/10" />
                        </div>
                        <div className="relative flex justify-center">
                          <span className="bg-[color:var(--np-bg,#F5EBDC)] px-3 font-mono-tech text-[9px] tracking-[0.3em] uppercase text-[color:var(--np-text-2)]">
                            or
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                  <button
                    data-testid="google-signin-btn"
                    onClick={handleGoogle}
                    disabled={busy || !agreed}
                    className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-black/20 px-5 py-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase hover:bg-[color:var(--np-text)] hover:text-[color:var(--np-bg)] transition-colors disabled:opacity-40"
                  >
                    <GoogleLogo size={16} weight="bold" />
                    {busy ? "Signing in…" : "Continue with Google"}
                  </button>
                  {!agreed && (
                    <p
                      data-testid="google-consent-hint"
                      className="text-[11px] text-[color:var(--np-brand)] text-center"
                    >
                      Tick both consent boxes below to enable sign-in.
                    </p>
                  )}
                </div>
              )}

              {tab === "email" && (
                <div>
                  {!emailSent ? (
                    <form onSubmit={handleEmail} className="space-y-3">
                      <label className="block font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-text-2)]">
                        Your email
                      </label>
                      <input
                        data-testid="email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@planet.earth"
                        className="w-full border border-black/20 bg-transparent px-4 py-3 font-serif-display text-lg focus:outline-none focus:border-[color:var(--np-brand)]"
                      />
                      <button
                        data-testid="email-link-btn"
                        type="submit"
                        disabled={busy || !agreed}
                        className="w-full rounded-full bg-[color:var(--np-text)] text-[color:var(--np-bg)] px-5 py-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase hover:opacity-90 disabled:opacity-40"
                      >
                        {busy ? "Sending…" : "Send sign-in link"}
                      </button>
                    </form>
                  ) : (
                    <div className="space-y-3 text-sm text-[color:var(--np-text-2)]">
                      <div className="font-serif-display text-2xl text-[color:var(--np-text)]">Check your inbox.</div>
                      <p>We sent a one-time sign-in link to <b className="text-[color:var(--np-text)]">{email}</b>. Open it on this device and tap <b>Confirm my email</b>.</p>
                      <p className="text-xs">The link is single-use — please don't preview it in another window before clicking.</p>
                    </div>
                  )}
                </div>
              )}

              {tab === "phone" && (
                <div>
                  {!otpSent ? (
                    <form onSubmit={handleSendOtp} className="space-y-3">
                      <label className="block font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-text-2)]">
                        Phone (with country code)
                      </label>
                      <input
                        data-testid="phone-input"
                        type="tel"
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+91 98765 43210"
                        className="w-full border border-black/20 bg-transparent px-4 py-3 font-serif-display text-lg focus:outline-none focus:border-[color:var(--np-brand)]"
                      />
                      <button
                        data-testid="send-otp-btn"
                        type="submit"
                        disabled={busy || !agreed}
                        className="w-full rounded-full bg-[color:var(--np-text)] text-[color:var(--np-bg)] px-5 py-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase hover:opacity-90 disabled:opacity-40"
                      >
                        {busy ? "Sending…" : "Send OTP"}
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleVerifyOtp} className="space-y-3">
                      <label className="block font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-text-2)]">
                        6-digit code
                      </label>
                      <input
                        data-testid="otp-input"
                        type="text" inputMode="numeric" maxLength={6} required
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                        className="w-full border border-black/20 bg-transparent px-4 py-3 font-serif-display text-3xl tracking-[0.5em] text-center focus:outline-none focus:border-[color:var(--np-brand)]"
                      />
                      <button
                        data-testid="verify-otp-btn"
                        type="submit"
                        disabled={busy}
                        className="w-full rounded-full bg-[color:var(--np-text)] text-[color:var(--np-bg)] px-5 py-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase hover:opacity-90 disabled:opacity-40"
                      >
                        {busy ? "Verifying…" : "Verify & sign in"}
                      </button>
                    </form>
                  )}
                  <div id="recaptcha-container" />
                </div>
              )}
            </div>

            {/* Sticky footer — consent checkboxes always visible so the
                Continue button never appears "dead" because a required
                box sits below the scroll fold (the bug that made the
                Google tab silently do nothing). */}
            <div
              className="shrink-0 border-t border-black/10 bg-[color:var(--np-bg)] px-6 md:px-8 py-4 space-y-2.5"
              data-testid="login-modal-consent-footer"
            >
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
                Required · read + agree
              </div>
              <label className="flex items-start gap-3 text-xs leading-snug text-[color:var(--np-text-2)]">
                <input
                  type="checkbox"
                  data-testid="terms-checkbox"
                  checked={termsChecked}
                  onChange={(e) => setTermsChecked(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[color:var(--np-brand)]"
                />
                <span>
                  I agree to the{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="terms-link"
                    className="underline text-[color:var(--np-text)] inline-flex items-center gap-1"
                  >
                    Terms of Service <ArrowSquareOut size={11} />
                  </Link>.
                </span>
              </label>
              <label className="flex items-start gap-3 text-xs leading-snug text-[color:var(--np-text-2)]">
                <input
                  type="checkbox"
                  data-testid="privacy-checkbox"
                  checked={privacyChecked}
                  onChange={(e) => setPrivacyChecked(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[color:var(--np-brand)]"
                />
                <span>
                  I agree to the{" "}
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="privacy-link"
                    className="underline text-[color:var(--np-text)] inline-flex items-center gap-1"
                  >
                    Privacy Notice <ArrowSquareOut size={11} />
                  </Link>.
                </span>
              </label>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
}
